/**
 * Send to edit (1.0.0-rc.6): a sequence, or a selection of clips, handed off to an NLE through an edit location.
 * PRISM writes a folder with the media, the timelines (FCP7 XML, FCPXML, OTIO, AAF, EDL), the graphics rendered as
 * ProRes 4444 with alpha, subtitles, prism-manifest.json and PRISM-README.txt. Check in (rc.7) brings the edit back.
 */

/** Timeline formats a hand-off can carry. */
export const NLE_FORMATS = ['fcp7', 'fcpxml', 'otio', 'aaf', 'edl'] as const;
export type NleFormat = (typeof NLE_FORMATS)[number];
export const NLE_FORMAT_INFO: Record<NleFormat, { label: string; extension: string; opens: string }> = {
  fcp7: { label: 'FCP7 XML', extension: 'xml', opens: 'Premiere Pro (File → Import), DaVinci Resolve' },
  fcpxml: { label: 'FCPXML 1.10', extension: 'fcpxml', opens: 'Final Cut Pro (File → Import → XML), DaVinci Resolve' },
  otio: { label: 'OpenTimelineIO', extension: 'otio', opens: 'DaVinci Resolve (File → Import → Timeline)' },
  aaf: { label: 'AAF', extension: 'aaf', opens: 'Avid Media Composer (import into a bin), DaVinci Resolve' },
  edl: { label: 'CMX 3600 EDL', extension: 'edl', opens: 'Avid, Resolve and most NLEs (one video track)' },
};

/**
 * The NLEs a hand-off is made for (rc.9 fixes): the dialog picks one, and the hand-off writes one timeline in the
 * format chosen from those it reads (the first is its best, and the default). Download timeline still makes any format.
 */
export const NLES = ['avid', 'premiere', 'resolve', 'fcp'] as const;
export type Nle = (typeof NLES)[number];
export const NLE_INFO: Record<Nle, { label: string; formats: readonly NleFormat[]; opens: string }> = {
  avid: { label: 'Avid Media Composer', formats: ['aaf', 'edl'], opens: 'import the .aaf into a bin (or the .edl, one video track), then link the media in media/' },
  premiere: { label: 'Adobe Premiere Pro', formats: ['fcp7', 'otio', 'edl'], opens: 'File → Import, choose the timeline' },
  resolve: { label: 'DaVinci Resolve', formats: ['fcpxml', 'otio', 'fcp7', 'edl'], opens: 'File → Import → Timeline, choose the timeline, and keep "Automatically import source clips into media pool" on' },
  fcp: { label: 'Final Cut Pro', formats: ['fcpxml'], opens: 'File → Import → XML, choose the .fcpxml' },
};
/**
 * The timeline's field order (1.0.0-rc.11): progressive, or interlaced with the upper (top) or lower (bottom) field
 * first, written as the sequence's field dominance (FCP7 XML), its format (FCPXML) and its metadata (OTIO, AAF). Only
 * 25 and 29.97 fps sequences can be interlaced (1080i50, 1080i59.94).
 */
export const HANDOFF_FIELD_ORDERS = ['progressive', 'upper', 'lower'] as const;
export type HandoffFieldOrder = (typeof HANDOFF_FIELD_ORDERS)[number];
export const HANDOFF_FIELD_ORDER_LABELS: Record<HandoffFieldOrder, string> = { progressive: 'Progressive', upper: 'Upper field first', lower: 'Lower field first' };

/** Whether a sequence at this rate can be handed off as an interlaced timeline (25 or 29.97 fps). */
export function interlacedTimelineRate(rate: { num: number; den: number }): boolean {
  const fps = rate.num / rate.den;
  return Math.abs(fps - 25) < 0.001 || Math.abs(fps - 30000 / 1001) < 0.001;
}

/**
 * The Send to edit dialog's default field order (1.0.0-rc.11): interlaced when most of V1's duration is interlaced
 * media, with the field order of the interlaced media that covers most of it; progressive otherwise, and always for
 * rates other than 25 and 29.97 fps. `scanOf` gives a version's probe (scanType, fieldOrder) when known.
 */
export function handoffFieldOrderDefault(
  doc: { frameRate: { num: number; den: number }; video: Array<{ versionId: string; sourceInTicks: number; sourceOutTicks: number }> },
  scanOf: (versionId: string) => { scanType?: string | null; fieldOrder?: string | null } | null | undefined,
): HandoffFieldOrder {
  if (!interlacedTimelineRate(doc.frameRate)) return 'progressive';
  let total = 0;
  const interlaced = { upper: 0, lower: 0 };
  for (const c of doc.video) {
    const d = Math.max(0, c.sourceOutTicks - c.sourceInTicks);
    total += d;
    const p = scanOf(c.versionId);
    if (p?.scanType !== 'interlaced') continue;
    // The first letter is the field shown first (tt and tb upper, bb and bt lower), as QC and export read it.
    interlaced[p.fieldOrder === 'bb' || p.fieldOrder === 'bt' ? 'lower' : 'upper'] += d;
  }
  if (!total || interlaced.upper + interlaced.lower <= total / 2) return 'progressive';
  return interlaced.lower > interlaced.upper ? 'lower' : 'upper';
}

/** The label of a format as an NLE's Format select shows it: XML, FCPXML, OTIO, AAF, EDL. */
export const NLE_FORMAT_SHORT: Record<NleFormat, string> = { fcp7: 'XML', fcpxml: 'FCPXML', otio: 'OTIO', aaf: 'AAF', edl: 'EDL' };

/**
 * What media goes with a hand-off: the proxies (small, for remote offices), the originals, trimmed originals (the
 * parts the sequence uses plus handles, rewrapped without transcoding), or none: the timelines link the originals
 * where they are (a storage location editors also mount, read-only).
 */
export const MEDIA_MODES = ['proxy', 'original', 'trimmed', 'in_place'] as const;
export type MediaMode = (typeof MEDIA_MODES)[number];
export const MEDIA_MODE_LABELS: Record<MediaMode, string> = {
  proxy: 'Proxies', original: 'Originals', trimmed: 'Trimmed originals with handles', in_place: 'In place (link the originals where they are)',
};

/** The editors' operating system: how the timelines write file paths (1.0.0-rc.9: from the chosen mapping). */
export const TARGET_OS = ['mac', 'windows', 'linux'] as const;
export type TargetOs = (typeof TARGET_OS)[number];
export const TARGET_OS_LABELS: Record<TargetOs, string> = { mac: 'macOS', windows: 'Windows', linux: 'Linux' };

export const HANDOFF_STATUSES = ['queued', 'running', 'ready', 'failed', 'cancelled'] as const;
export type HandoffStatus = (typeof HANDOFF_STATUSES)[number];

export interface HandoffDTO {
  id: string;
  /** A sequence, or clips sent without one (a Library selection or a bin). */
  kind: 'sequence' | 'clips';
  name: string;
  sequenceId: string | null;
  /** The pinned version of the cut that was sent (sequence_versions.kind 'handoff'). */
  sequenceVersionId: string | null;
  assetIds: string[];
  /** Where it was written: a server folder, a folder on a storage agent, or (1.0.0-rc.9) an S3 or OCI bucket. */
  location: { id: string; name: string; site: string; kind: 'fs' | 'agent' | 's3' | 'oci' };
  /** The hand-off's folder inside the edit location. */
  folder: string;
  /** Where editors find it (the hand-off's mapping joined with its folder), or null when it was sent without a mapping. */
  workstationFolder: string | null;
  /** The workstation mapping its timelines use (1.0.0-rc.9; a snapshot of the location's when it was sent). */
  mapping: import('./workstation.ts').WorkstationMappingSnapshot | null;
  /** The edit location's mappings now (1.0.0-rc.9): Download timeline makes a timeline for any of them. */
  mappings: import('./workstation.ts').WorkstationMappingSnapshot[];
  /** The timelines written (ready hand-offs): their format and path in the folder, for Download timeline and Copy path. */
  timelines: Array<{ format: NleFormat; path: string }>;
  formats: NleFormat[];
  /** The NLE it was made for (rc.9 fixes); null for hand-offs sent with a list of formats. */
  nle: Nle | null;
  mediaMode: MediaMode;
  handlesTicks: number;
  targetOs: TargetOs;
  subtitles: boolean;
  /** The timelines' field order (1.0.0-rc.11); progressive for hand-offs sent before it. */
  fieldOrder: HandoffFieldOrder;
  /** The sequence format the timelines were made for (1.7.0): `'primary'` or a format id; null or left out for the primary. */
  formatId?: string | null;
  status: HandoffStatus;
  step: string | null;
  percent: number | null;
  error: { code: string; message: string; hint?: string } | null;
  /** What could not be included or was approximated (an AAF that could not be written, missing workstation paths…). */
  warnings: string[];
  files: { count: number; bytes: number };
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  finishedAt: string | null;
  /** The timelines, subtitles, manifest and README (and the media, for small proxy hand-offs) as a zip; null until ready. */
  packageUrl: string | null;
  /** Whether the zip carries the media too (proxies under the size limit). */
  packageHasMedia: boolean;
  /** When an edit was last checked in from it (1.0.0-rc.7). */
  checkedInAt: string | null;
  /** When its folder was cleaned up after retention (1.0.0-rc.7): nothing more can be checked in from it. */
  cleanedAt: string | null;
  /**
   * Fixes after rc.9 testing: what the last checks of its folder found. `missing`: two definite "not found" answers
   * minutes apart (hidden in Import NLE sequence by default); `unreachable`: the last check could not answer (the
   * storage agent offline…), which never counts as missing. Null until checked.
   */
  folderCheck: { missing: boolean; missingSince: string | null; notFound: number; checkedAt: string | null; unreachable: string | null } | null;
  /**
   * Removed from the Import list (fixes after rc.9 testing): hidden from the Import NLE sequence picker, left out of
   * cleanup and the watch. Its files stay, and the sequence's Exports list still shows it (it can be checked in there).
   */
  removedAt: string | null;
  /** Whether I may remove it from the Import list (the sender, or storage.manage as for cleanup). */
  canRemove: boolean;
  /** Whether I may show it in the Import list again (removed, and the sender or storage.manage). */
  canRestore: boolean;
}

export interface HandoffFileDTO {
  path: string;
  kind: 'media' | 'graphic' | 'timeline' | 'subtitles' | 'manifest' | 'readme';
  sizeBytes: number | null;
  status: 'pending' | 'written' | 'failed' | 'skipped';
}

/** An edit location offered in the Send to edit dialog. */
export interface EditDestinationDTO {
  id: string;
  name: string;
  /** A server folder, a folder on a storage agent, or (1.0.0-rc.9) an S3 or OCI bucket. */
  kind: 'fs' | 'agent' | 's3' | 'oci';
  site: string;
  availability: 'online' | 'offline' | 'unknown';
  /** The workspace's default edit location. */
  isDefault: boolean;
  /** How editors open it (1.0.0-rc.9): the dialog picks one, and the timelines use its paths. */
  workstationMappings: import('./workstation.ts').WorkstationMappingDTO[];
  /** Free space where the location reports it (a bucket: what is left of its quota, null without one). */
  freeBytes: number | null;
  /** Why it cannot be used now (an agent without the edit base, disconnected…), or null. */
  problem: string | null;
}
